import { useState, useEffect, useRef, useCallback } from 'react'
import { useUserId, useAuth, ToastyLoading, useSubscriptionWithLoading } from '@talktoast/shared'
import { usePreferences } from '@talktoast/shared'
import { mealPlanningService } from '@talktoast/shared'
import { type MealPlanResponse, type RecipeDetailResponse } from '@talktoast/shared'
import { backendApiService } from '@talktoast/shared'
import { sessionApiService } from '@talktoast/shared'
import MealPlanDisplay from './MealPlanDisplay'
// DEPRECATED: Recipe parsing now handled by backend
// import { parseRecipeResponse, isRecipeResponse } from '../utils/recipeParser'
import { SessionPreferences } from '@talktoast/shared'
import { Button, RecipeCardSkeleton } from '@talktoast/ui'
import { showError } from '../utils/toast'
import { ICONS, getIconSize, getIconColor } from '@talktoast/shared'
import { RecipeModifications } from '../types/recipe'

interface MealPlanningChatProps {
  sessionPreferences: SessionPreferences | null
  initialMealSessionId?: string | null  // Meal session ID created by parent component
  isResuming?: boolean  // Flag indicating if resuming existing session (vs creating new)
  onBack: () => void
  onContinue?: (recipeDetails: Map<string, RecipeDetailResponse>, sessionId: string) => void
  onNavigateToSubscription?: () => void
}

export default function MealPlanningChat({ sessionPreferences, initialMealSessionId, isResuming = false, onBack, onContinue, onNavigateToSubscription }: MealPlanningChatProps) {
  const userId = useUserId() // Custom UUID architecture - works for both guest and authenticated users
  const { isGuest } = useAuth()
  const { getPersistentPreferences } = usePreferences()
  const { refreshSubscription, optimisticIncrementRerolls } = useSubscriptionWithLoading()
  const [isLoading, setIsLoading] = useState(false)
  const [sessionId, setSessionId] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [showDebug, setShowDebug] = useState(false)
  const [debugLogs, setDebugLogs] = useState<string[]>([])
  const [isInitializing, setIsInitializing] = useState(false)
  const [recipeDetails, setRecipeDetails] = useState<Map<string, RecipeDetailResponse>>(new Map())
  const [loadingRecipeDetails, setLoadingRecipeDetails] = useState<Set<string>>(new Set())
  const [mealSessionId, setMealSessionId] = useState<string | null>(null)
  const [addingSlotForMealType, setAddingSlotForMealType] = useState<string | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const [hasRecipesLoaded, setHasRecipesLoaded] = useState(false)
  const initializationStartedRef = useRef(false)
  const mealSessionCreationStartedRef = useRef(false)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  // Tracks the recipe count at the moment an add-slot request is sent so the
  // skeleton stays visible until the polling loop detects the new recipe.
  const addSlotPrevCountRef = useRef<number | null>(null)
  const shownRecipeErrorsRef = useRef<Set<string>>(new Set())

  const addDebugLog = (message: string) => {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0]
    setDebugLogs(prev => [...prev.slice(-49), `[${timestamp}] ${message}`]) // Keep last 50 logs
  }

  // Polling functions for recipe status
  // Define stopPolling first to avoid circular dependency
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    setIsPolling(false)
    console.log('[MealPlanningChat] Stopped polling')
  }, [])

  const startPolling = useCallback((sid?: string) => {
    const effectiveSessionId = sid || sessionId
    if (pollingIntervalRef.current || !effectiveSessionId) {
      console.log('[MealPlanningChat] Polling skipped - intervalRef:', !!pollingIntervalRef.current, 'effectiveSessionId:', effectiveSessionId)
      return
    }

    setIsPolling(true)
    const pollingStartTime = Date.now()
    const maxPollingDuration = 60000 // 60 seconds
    console.log('[MealPlanningChat] Starting recipe polling for session:', effectiveSessionId)

    pollingIntervalRef.current = setInterval(async () => {
      try {
        console.log(`[MealPlanningChat] Polling recipes for session: ${effectiveSessionId}`)
        const recipes = await backendApiService.getSessionRecipes(effectiveSessionId)

        if (!recipes || !Array.isArray(recipes)) {
          console.warn('[MealPlanningChat] No recipes in poll response')
          return
        }

        console.log(`[MealPlanningChat] Poll result: ${recipes.length} recipes`)

        // Update recipe details map and track completion
        const newRecipeDetails = new Map<string, RecipeDetailResponse>()
        const newLoadingSet = new Set<string>()
        let allComplete = true

        recipes.forEach((recipe: any) => {
          if (recipe.status === 'ready' && recipe.ingredients && recipe.ingredients.length > 0) {
            // Recipe is complete
            newRecipeDetails.set(recipe.recipe_name || recipe.name, {
              recipe: recipe,
              sessionId: sessionId,
              kitchenTools: recipe.kitchenTools || [],
              cookingTips: recipe.cookingTips || []
            })
            console.log(`[MealPlanningChat] Recipe ready: ${recipe.recipe_name || recipe.name}`)
          } else if (recipe.status === 'fetching') {
            // Recipe still loading
            allComplete = false
            newLoadingSet.add(recipe.recipe_name || recipe.name)
            console.log(`[MealPlanningChat] Recipe fetching: ${recipe.recipe_name || recipe.name}`)
          } else if (recipe.status === 'error') {
            const erroredName = recipe.recipe_name || recipe.name
            console.error(`[MealPlanningChat] Recipe error: ${erroredName}`, recipe.error_message)
            // Show toast once per failed recipe and clear any stuck add-slot skeleton
            if (!shownRecipeErrorsRef.current.has(erroredName)) {
              shownRecipeErrorsRef.current.add(erroredName)
              showError(`Failed to generate "${erroredName}". Use the + button to try again.`)
              if (addSlotPrevCountRef.current !== null) {
                addSlotPrevCountRef.current = null
                setAddingSlotForMealType(null)
              }
            }
          }
        })

        setRecipeDetails(newRecipeDetails)
        setLoadingRecipeDetails(newLoadingSet)

        // Stop polling when all recipes are complete.
        // Note: 'error' status is intentionally treated as terminal — it does NOT
        // set allComplete = false. Any new status values added in future should
        // consciously decide whether they block completion (i.e. set allComplete = false).
        if (allComplete && recipes.length > 0) {
          console.log('[MealPlanningChat] All recipes complete, stopping poll')
          setHasRecipesLoaded(true)
          stopPolling()
          return
        }

        // Stop polling after max duration (60 seconds)
        const elapsedTime = Date.now() - pollingStartTime
        if (elapsedTime >= maxPollingDuration) {
          console.warn(`[MealPlanningChat] Polling timeout after ${elapsedTime}ms - ${newLoadingSet.size} recipes still loading`)
          setHasRecipesLoaded(true) // Always exit skeleton on timeout, even with partial results
          stopPolling()
          // Keep partial results - recipes that completed will be shown
        }
      } catch (error) {
        console.error('[MealPlanningChat] Polling error:', error)
      }
    }, 2500) // Poll every 2.5 seconds
  }, [sessionId, stopPolling])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling()
    }
  }, [stopPolling])

  // Clear the add-slot skeleton once polling delivers the new recipe.
  // addSlotPrevCountRef is set just before the processMessage call and reset
  // here so the skeleton stays visible during the ~2.5s poll gap.
  useEffect(() => {
    if (addingSlotForMealType !== null && addSlotPrevCountRef.current !== null) {
      if (recipeDetails.size > addSlotPrevCountRef.current) {
        addSlotPrevCountRef.current = null
        setAddingSlotForMealType(null)
      }
    }
  }, [recipeDetails, addingSlotForMealType])

  useEffect(() => {
    // Initialize meal planning session and generate initial meal plan
    const initializeSession = async () => {
      console.log('[MealPlanningChat] Session initialization check:', {
        isInitializing,
        sessionId: sessionId || 'EMPTY',
        sessionPreferences: !!sessionPreferences,
        userId: userId || 'none'
      })

      // Use ref to prevent duplicate initialization from React StrictMode
      if (initializationStartedRef.current || isInitializing || sessionId) {
        console.log('[MealPlanningChat] Already initializing or session exists, skipping duplicate call')
        return
      }

      if (!sessionPreferences) {
        console.log('[MealPlanningChat] No session preferences available, skipping initialization')
        return
      }

      // CRITICAL FIX: Wait for initialMealSessionId to be available
      if (!initialMealSessionId) {
        console.log('[MealPlanningChat] Waiting for meal session ID from parent component...')
        return // Component will re-run this effect when initialMealSessionId becomes available
      }

      // Set ref immediately (synchronous) to prevent double initialization
      initializationStartedRef.current = true
      setIsInitializing(true)
      console.log('[MealPlanningChat] Starting session initialization with meal session ID:', initialMealSessionId)
      
      try {
        // Test server connectivity first
        addDebugLog('Testing server connectivity...')
        const isConnected = await backendApiService.testConnectivity()
        if (!isConnected) {
          addDebugLog('SERVER CONNECTIVITY FAILED - API requests will not work')
          addDebugLog('Check browser Network tab for detailed error information')
        } else {
          addDebugLog('Server connectivity test PASSED')
        }

        const persistentPrefs = getPersistentPreferences()
        const sessionPrefs = sessionPreferences

        // Debug log preferences
        console.log('[MealPlanningChat] Got preferences:', {
          userId,
          persistentPrefs,
          sessionPrefs,
          numberOfPeople: sessionPrefs?.numberOfPeople,
          enabledMealTypes: sessionPrefs?.mealTypes.filter(m => m.enabled).map(m => m.type),
          mealCounts: sessionPrefs?.mealCounts
        })
        addDebugLog(`Persistent prefs: ${JSON.stringify(persistentPrefs)}`)
        addDebugLog(`Session prefs: ${JSON.stringify(sessionPrefs)}`)

        if (!sessionPrefs) {
          throw new Error('Session preferences not available')
        }

        // Create LLM session using the meal session ID (already validated above)
        // initialMealSessionId is guaranteed to exist at this point
        const session = await mealPlanningService.createSession(
          userId,
          initialMealSessionId,
          persistentPrefs,
          sessionPrefs
        )

        // Use the same ID for both LLM session and meal session
        setSessionId(session.sessionId)
        setMealSessionId(session.sessionId)
        mealSessionCreationStartedRef.current = true
        console.log('[MealPlanningChat] Using unified session ID:', session.sessionId)

        // Check if we're resuming an existing session
        if (isResuming) {
          console.log('[MealPlanningChat] Resuming existing session - loading recipes from database')
          addDebugLog('Resuming session - loading existing recipes from database')

          // Don't generate new meal plan, just load existing recipes via polling.
          // hasRecipesLoaded stays false until the first poll tick returns data,
          // keeping the skeleton visible and preventing the premature empty state.
          startPolling(session.sessionId)
          setIsLoading(false)
        } else {
          // New session - generate meal plan
          setIsLoading(true)

          // Generate initial meal plan based on preferences
          const initialMessage = "Based on my preferences, generate a meal plan with dish names first, then I'll ask for specific recipes"

          try {
            const debugInfo = {
              persistentPrefs,
              sessionPrefs,
              userId,
              sessionId: session.sessionId
            }
            console.log('[Debug] Initializing meal planning session with preferences:', debugInfo)
            addDebugLog(`Initializing session: ${JSON.stringify(debugInfo)}`)

            setError(null)
            const response: MealPlanResponse = await mealPlanningService.processMessage(
              session.sessionId,
              initialMessage,
              userId
            )

            // DON'T add initial message to chat - user wants no text until they type
            console.log('[Debug] Initial meal plan generated successfully')
            addDebugLog('Initial meal plan generated successfully')
            addDebugLog(`API Response: ${response.response}`)

            // Start polling for recipe details (backend handles fetching)
            if (response.recipes && response.recipes.length > 0) {
              console.log(`[MealPlanningChat] ${response.recipes.length} recipes in response, starting poll`)
              startPolling(session.sessionId)
            }
          } catch (error) {
            const errorInfo = {
              error: error.message,
              userId,
              sessionId: session.sessionId,
              retryCount
            }
            console.error('[Debug] Error generating initial meal plan:', errorInfo)
            addDebugLog(`ERROR in initial meal plan: ${JSON.stringify(errorInfo)}`)

            setError(error.message || 'Failed to generate meal plan')
          } finally {
            setIsLoading(false)
          }
        } // End of else block (new session)
        
      } catch (error) {
        console.error('Failed to initialize meal planning session:', error)
        setIsLoading(false)
      } finally {
        setIsInitializing(false)
      }
    }

    initializeSession()
  }, [userId, sessionPreferences, initialMealSessionId]) // Added initialMealSessionId dependency

  const handleRemoveRecipe = async (recipeName: string) => {
    const sessionIdToUse = mealSessionId || initialMealSessionId || sessionId
    if (!sessionIdToUse) {
      showError('Cannot remove recipe — session not available.')
      return
    }

    console.log('[MealPlanningChat] Removing recipe:', recipeName)
    addDebugLog(`Removing recipe: ${recipeName}`)

    // Capture snapshot before optimistic delete so we can roll back on failure
    const snapshot = new Map(recipeDetails)

    // Optimistically remove from UI immediately
    setRecipeDetails(prev => {
      const updated = new Map(prev)
      updated.delete(recipeName)
      return updated
    })

    try {
      // Call API to remove from database
      await backendApiService.deleteSessionRecipe(sessionIdToUse, recipeName)
      console.log('[MealPlanningChat] Recipe removed successfully:', recipeName)
    } catch (error) {
      console.error('[MealPlanningChat] Failed to remove recipe:', error)
      addDebugLog(`Failed to remove recipe: ${error.message}`)
      // Restore snapshot on failure so the user sees their original plan
      setRecipeDetails(snapshot)
      showError('Failed to remove recipe. Please try again.')
    }
  }

  const handleAddSlot = async (mealType: string) => {
    if (!sessionId || sessionId.trim() === '') {
      showError('Cannot add a recipe — session not available.')
      return
    }

    console.log('[MealPlanningChat] Adding slot for meal type:', mealType)
    addDebugLog(`Adding slot for meal type: ${mealType}`)

    // Record the count before the request so the useEffect above can detect
    // when polling has delivered the new recipe and clear the skeleton.
    addSlotPrevCountRef.current = recipeDetails.size
    setAddingSlotForMealType(mealType)

    try {
      // Use the dedicated add-slot endpoint — bypasses chat/tag-parsing so the
      // slot never gets stuck in 'fetching' if Claude returns a conversational reply.
      await backendApiService.addRecipeSlot(sessionId, mealType as 'breakfast' | 'lunch' | 'dinner' | 'snacks')

      console.log('[MealPlanningChat] Add-slot request accepted for:', mealType)

      // Start polling to load the new recipe.
      // Do NOT clear addingSlotForMealType here — the useEffect watching
      // recipeDetails will clear it once the new recipe appears, eliminating
      // the gap between the API response and the first poll tick (~2.5s).
      // Pass sessionId explicitly to avoid racing against async state flush.
      stopPolling()
      startPolling(sessionId)
    } catch (error) {
      console.error('[MealPlanningChat] Failed to add slot:', error)
      addDebugLog(`Failed to add slot: ${error.message}`)
      const isRateLimited = error.message?.includes('429') || error.status === 429
      setError(isRateLimited
        ? "You're adding recipes too quickly — please wait a moment and try again."
        : error.message || 'Failed to add recipe')
      // Clear skeleton immediately on error (no new recipe is coming)
      addSlotPrevCountRef.current = null
      setAddingSlotForMealType(null)
    }
  }

  const handleRetry = async () => {
    if (!sessionId) return

    console.log('[Debug] Retrying initialization')
    addDebugLog(`Retrying initialization (attempt ${retryCount + 1})`)
    setRetryCount(prev => prev + 1)
    setIsLoading(true)
    setError(null)

    try {
      const initialMessage = "Based on my preferences, generate a meal plan with dish names first, then I'll ask for specific recipes"

      await mealPlanningService.processMessage(
        sessionId,
        initialMessage,
        userId
      )

      setError(null)
      setRetryCount(0)
      console.log('[Debug] Retry successful')
      addDebugLog('Retry successful')
      startPolling()
    } catch (error) {
      console.error('[Debug] Retry failed:', error)
      addDebugLog(`Retry failed: ${error.message}`)
      setError(error.message || 'Retry failed')
    } finally {
      setIsLoading(false)
    }
  }

  // Helper function to create or get meal session for recipe storage
  const ensureMealSession = async () => {
    // First check if we have mealSessionId in state
    if (mealSessionId) {
      return mealSessionId
    }

    // If not in state yet, check if we have the initial prop (during initialization)
    if (initialMealSessionId) {
      console.log('[MealPlanningChat] Using initialMealSessionId for early recipe saves:', initialMealSessionId)
      return initialMealSessionId
    }

    // Prevent duplicate meal session creation from concurrent calls
    if (mealSessionCreationStartedRef.current) {
      console.log('[MealPlanningChat] Meal session creation already in progress, waiting...')
      // Wait for the ongoing creation to finish by polling mealSessionId
      let attempts = 0
      while (!mealSessionId && attempts < 50) {
        await new Promise(resolve => setTimeout(resolve, 100))
        attempts++
      }
      return mealSessionId || initialMealSessionId
    }

    try {
      mealSessionCreationStartedRef.current = true

      const persistentPrefs = getPersistentPreferences()

      console.log('[MealPlanningChat] Debugging preferences for meal session:', {
        userId,
        sessionPreferences: sessionPreferences,
        persistentPrefs: persistentPrefs,
        hasSessionPrefs: !!sessionPreferences,
        hasPersistentPrefs: !!persistentPrefs
      })

      if (!sessionPreferences || !persistentPrefs) {
        console.error('[MealPlanningChat] Missing preferences for meal session creation:', {
          sessionPreferencesPresent: !!sessionPreferences,
          persistentPrefsPresent: !!persistentPrefs,
          sessionPreferencesValue: sessionPreferences,
          persistentPrefsValue: persistentPrefs
        })
        return null
      }

      console.log('[MealPlanningChat] Creating meal session with:', {
        userId,
        hasSessionPrefs: !!sessionPreferences,
        hasPersistentPrefs: !!persistentPrefs
      })

      const result = await backendApiService.createOrGetMealSession(
        isGuest ? null : userId, // userId only for authenticated users
        isGuest ? userId : null, // guestSessionId for guest users
        sessionPreferences,
        persistentPrefs
      )

      setMealSessionId(result.session.id)
      console.log('[MealPlanningChat] Meal session created/retrieved:', result.session.id)
      return result.session.id
    } catch (error) {
      console.error('[MealPlanningChat] Failed to create/get meal session:', error)
      console.error('[MealPlanningChat] Error details:', {
        message: error.message,
        userId,
        hasSessionPrefs: !!sessionPreferences,
        hasPersistentPrefs: !!getPersistentPreferences(),
        errorStack: error.stack
      })
      // Don't return null immediately, try to continue without database persistence
      console.warn('[MealPlanningChat] Continuing without database session - recipes will be memory-only')
      return null
    }
  }


  const handleContinueToShopping = async () => {
    console.log('[MealPlanningChat] Continue to Shopping clicked', {
      onContinue: !!onContinue,
      mealSessionId,
      initialMealSessionId,
      sessionId,
      recipeDetailsCount: recipeDetails.size,
    })

    if (!onContinue) {
      console.error('[MealPlanningChat] No onContinue callback provided')
      return
    }

    // Ensure we have a meal session ID - this should NEVER be null at this point
    if (!mealSessionId) {
      console.error('[MealPlanningChat] CRITICAL ERROR: No meal session ID available', {
        mealSessionId,
        initialMealSessionId,
        sessionId,
      })

      // Try fallback to initialMealSessionId or sessionId
      const fallbackSessionId = initialMealSessionId || sessionId
      if (fallbackSessionId) {
        console.warn('[MealPlanningChat] Using fallback session ID:', fallbackSessionId)
        onContinue(recipeDetails, fallbackSessionId)
        return
      }

      // Do not continue - this is a critical error
      showError('Unable to proceed to shopping. Session ID is missing.')
      return
    }

    try {
      // Call onContinue with meal session ID
      console.log('[MealPlanningChat] Continuing to shopping with meal session ID:', mealSessionId)
      onContinue(recipeDetails, mealSessionId)
    } catch (error) {
      console.error('[MealPlanningChat] Error in handleContinueToShopping:', error)
      // Continue anyway with the meal session ID - don't block the user
      onContinue?.(recipeDetails, mealSessionId)
    }
  }

  const handleRerollRecipe = async (recipeId: string, recipeName: string, mealType: string, modifications?: RecipeModifications) => {
    console.log('[MealPlanningChat] Re-roll attempt started:', {
      recipeId,
      recipeName,
      mealType,
      sessionId,
      hasSessionId: !!sessionId,
      modifications
    })

    if (!sessionId || sessionId.trim() === '') {
      console.error('[MealPlanningChat] CRITICAL: Cannot reroll recipe - no sessionId available', {
        sessionId,
        hasSessionId: !!sessionId,
        sessionIdType: typeof sessionId,
        sessionIdLength: sessionId?.length || 0
      })
      addDebugLog(`ERROR: Cannot reroll recipe - no sessionId available (sessionId: "${sessionId}")`)
      setError('Cannot reroll recipe - session not available. Please try starting a new conversation.')
      return
    }

    console.log('[Debug] Rerolling recipe:', { recipeId, recipeName, mealType, modifications, sessionId })
    addDebugLog(`Rerolling recipe: ${recipeName} ${modifications ? 'with modifications' : ''} (sessionId: ${sessionId})`)

    setIsLoading(true)

    try {
      // Build reroll message based on modification type
      let rerollMessage = ''

      if (modifications) {
        // Sanitize AI-sourced modification values before embedding them in the
        // prompt to prevent prompt injection. Strip newlines and cap length.
        const sanitize = (value: string | undefined): string | undefined =>
          value ? value.replace(/[\r\n]/g, ' ').trim().slice(0, 100) : value

        // Full dish replacement: user asked Toasty for a completely different recipe.
        if (modifications.targetDish) {
          const sanitizedDish = sanitize(modifications.targetDish)!
          rerollMessage = `Please replace "${recipeName}" entirely with: ${sanitizedDish}. Generate a complete ${mealType} recipe for ${sanitizedDish}, following my dietary preferences and restrictions.`

          console.log('[MealPlanningChat] Full replace reroll:', { from: recipeName, to: sanitizedDish })
          addDebugLog(`Full replace reroll: "${recipeName}" → "${sanitizedDish}"`)
        } else {

        const sanitizedModifications = {
          ...modifications,
          protein: sanitize(modifications.protein),
          cookingMethod: sanitize(modifications.cookingMethod),
          cuisine: sanitize(modifications.cuisine),
        }

        const hasProteinChange = sanitizedModifications.protein
        const hasMethodChange = sanitizedModifications.cookingMethod
        const hasCuisineChange = sanitizedModifications.cuisine

        // If only protein/method changes, modify the existing recipe
        if ((hasProteinChange || hasMethodChange) && !hasCuisineChange) {
          const changes = []
          if (hasProteinChange) {
            changes.push(`change the protein to ${sanitizedModifications.protein}`)
          }
          if (hasMethodChange) {
            changes.push(`change the cooking method to ${sanitizedModifications.cookingMethod}`)
          }

          rerollMessage = `🚨 CRITICAL OVERRIDE INSTRUCTION 🚨

The user has EXPLICITLY selected a cooking method/protein change. You MUST apply this change even if it requires equipment not in the standard appliance list.

MODIFICATION REQUEST:
- Original Recipe: "${recipeName}"
- Required Changes: ${changes.join(' and ')}
${hasMethodChange ? `\n- MANDATORY COOKING METHOD: "${sanitizedModifications.cookingMethod}" - User confirmed they have access to this cooking method for this specific meal` : ''}
${hasProteinChange ? `\n- MANDATORY PROTEIN: "${sanitizedModifications.protein}"` : ''}

REQUIREMENTS:
1. Keep the same core dish (e.g., "Steak", "Chicken Breast", etc.)
2. Keep similar flavors and seasonings
3. MANDATORY: ${hasMethodChange ? `Change the cooking method to "${sanitizedModifications.cookingMethod}"` : 'Keep same cooking method'}
4. MANDATORY: ${hasProteinChange ? `Change the protein to "${sanitizedModifications.protein}"` : 'Keep same protein'}
5. The recipe name MUST clearly reflect the new ${hasMethodChange ? 'cooking method' : 'protein'}

${hasMethodChange ? `⚠️ APPLIANCE OVERRIDE: For this specific modification, the user has confirmed they have access to use "${sanitizedModifications.cookingMethod}". Disregard the standard appliance availability list for this request.` : ''}

EXAMPLES:
- "Pan Seared Steak" + change to grilled → "Grilled Steak with Chimichurri" ✓
- "Baked Chicken" + change to stovetop → "Pan-Seared Chicken Breast" ✓
- "Grilled Salmon" + change to baked → "Oven-Baked Salmon" ✓

This is a MODIFICATION request, not a complete recipe change. Same dish, different ${hasMethodChange ? 'cooking method' : 'protein'}.`
        }
        // If cuisine is changed, generate a new recipe with the cuisine constraint
        else if (hasCuisineChange) {
          rerollMessage = `Please give me a different ${mealType} recipe to replace "${recipeName}". The new recipe must be ${sanitizedModifications.cuisine} cuisine style and follow my dietary preferences and restrictions.`

          // Also include protein/method if specified
          const additionalConstraints = []
          if (hasProteinChange) {
            additionalConstraints.push(`use ${sanitizedModifications.protein} as the protein`)
          }
          if (hasMethodChange) {
            additionalConstraints.push(`use ${sanitizedModifications.cookingMethod} as the cooking method`)
          }

          if (additionalConstraints.length > 0) {
            rerollMessage += ` Additionally, please ${additionalConstraints.join(' and ')}.`
          }
        }
        // Fallback for any other combination
        else {
          rerollMessage = `Please give me a different ${mealType} recipe to replace "${recipeName}". The new recipe must follow my dietary preferences and restrictions.`
        }
        } // end of else (non-targetDish modifications)
      } else {
        // No modifications, just a regular reroll
        rerollMessage = `Please give me a different ${mealType} recipe to replace "${recipeName}". The new recipe must follow my dietary preferences and restrictions.`
      }

      rerollMessage += `\n\nPlease return the response in the exact same format as the initial meal plan:\n\n**${mealType.toUpperCase()} (1 meal needed):**\n- [New Recipe Name]\n\n---END_RECIPES---`

      setLoadingRecipeDetails(prev => {
        const updated = new Set(prev)
        updated.add(recipeName)
        return updated
      })

      console.log('🔥 [MealPlanningChat] About to call mealPlanningService.processMessage:', {
        sessionId,
        userId,
        messageLength: rerollMessage.length,
        messagePreview: rerollMessage.substring(0, 100) + '...'
      })

      const response: MealPlanResponse = await mealPlanningService.processMessage(
        sessionId,
        rerollMessage,
        userId,
        { recipeToReplace: recipeName }
      )

      console.log('✅ [MealPlanningChat] Got response from mealPlanningService:', {
        responseLength: response.response.length,
        sessionId: response.sessionId,
        recipesCount: response.recipes?.length || 0,
        recipeAction: response.recipeAction
      })

      // Backend now handles recipe parsing and database updates
      // The response.recipes array contains the updated recipe list from the backend

      // Remove old recipe from frontend state
      setRecipeDetails(prev => {
        const updated = new Map(prev)
        updated.delete(recipeName)
        return updated
      })

      addDebugLog(`Recipe re-roll request sent successfully. Backend will process and fetch details.`)

      // If the backend found no alternative in the DB, surface an error and bail out.
      if (response.noMatch === true) {
        showError('No alternatives available right now. Upgrade for AI-powered suggestions.')
        // Restore the removed recipe from the poll so the user sees their original plan
        stopPolling()
        startPolling()
        return
      }

      // Optimistically increment the global weekly reroll counter immediately so
      // the UI updates without waiting for the refreshSubscription network round-trip.
      if (response.rerollTracking != null && !response.rerollTracking.unlimited) {
        optimisticIncrementRerolls()
      }

      // Full refresh to sync any other usage changes (non-blocking; overwrites the
      // optimistic increment with the authoritative server value once it completes).
      refreshSubscription().catch(() => {/* non-blocking */})

      // Start polling to update UI as the new recipe details are fetched by backend.
      // Stop any in-flight poll first so the isPolling guard in startPolling() doesn't
      // silently no-op when the previous reroll's poll is still running (bug #132).
      console.log(`[MealPlanningChat] Re-roll complete, source: ${response.rerollSource ?? 'unknown'}`)
      stopPolling()

      if (response.rerollSource === 'db') {
        // Recipe from DB is already in 'ready' state — no polling needed.
        // Trigger a one-shot session recipes refresh to update the UI immediately.
        console.log('[MealPlanningChat] DB reroll — refreshing session recipes directly')
        backendApiService.getSessionRecipes(sessionId).then((recipes) => {
          if (!recipes || !Array.isArray(recipes)) return
          const newRecipeDetails = new Map<string, RecipeDetailResponse>()
          const newLoadingSet = new Set<string>()
          recipes.forEach((recipe: any) => {
            if (recipe.status === 'ready' && recipe.ingredients && recipe.ingredients.length > 0) {
              newRecipeDetails.set(recipe.recipe_name || recipe.name, {
                recipe,
                sessionId,
                kitchenTools: recipe.kitchenTools || [],
                cookingTips: recipe.cookingTips || []
              })
            } else if (recipe.status === 'fetching') {
              newLoadingSet.add(recipe.recipe_name || recipe.name)
            }
          })
          setRecipeDetails(newRecipeDetails)
          setLoadingRecipeDetails(newLoadingSet)
          setHasRecipesLoaded(true)
        }).catch((err) => {
          console.warn('[MealPlanningChat] DB reroll recipe refresh failed, falling back to poll:', err)
          startPolling()
        })
      } else {
        // 'ai' source or unknown — use the existing polling path.
        startPolling()
      }
    } catch (error) {
      console.error('[Debug] Recipe reroll failed:', error)
      addDebugLog(`Recipe reroll failed: ${error.message}`)

      setIsLoading(false)
      setLoadingRecipeDetails(prev => {
        const updated = new Set(prev)
        updated.delete(recipeName)
        return updated
      })

      // Handle weekly budget exhausted (403 from backend)
      if (error.status === 403 || error.message?.includes('403') || error.message?.toLowerCase().includes('limit')) {
        setError("You've used all your rerolls for this week. Resets Friday.")
      } else {
        setError(error.message || 'Recipe reroll failed')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Helper functions for header display

  // During initial load (skeleton screen) use the preference-based count as a placeholder.
  // Once recipes are loaded, derive the count from live state so that chat add/remove
  // operations (which update recipeDetails / loadingRecipeDetails) are reflected immediately.
  const preferenceMealCount = sessionPreferences?.mealTypes
    .filter(mt => mt.enabled)
    .reduce((sum, mt) => sum + (sessionPreferences.mealCounts[mt.type] || 0), 0) || 0

  const totalMealCount = hasRecipesLoaded
    ? recipeDetails.size + loadingRecipeDetails.size
    : preferenceMealCount

  // Show loading screen until all recipes in the initial meal plan are fully loaded
  if (!hasRecipesLoaded) {
    return (
      <div className="flex flex-col min-h-screen md:min-h-[600px] w-full max-w-none bg-warm-cream">
        {/* Sticky Header */}
        <div className="sticky top-0 z-20 flex items-center justify-between px-4 py-3 border-b bg-white shadow-sm">
          {/* Left: Back button */}
          <Button variant="ghost" size="sm" onClick={onBack} className="flex items-center gap-1">
            <ICONS.navigation.back className={getIconSize('sm')} />
            <span className="hidden sm:inline">Back</span>
          </Button>

          {/* Center: People + Meal summary */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="flex items-center gap-1">
              <ICONS.user.users className={getIconSize('sm')} />
              <span>{sessionPreferences?.numberOfPeople || 2}</span>
            </span>
            <span className="text-gray-400">•</span>
            <span>
              {totalMealCount} meal{totalMealCount !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Right: Progress dots */}
          <div className="flex items-center gap-1">
            {Array.from({ length: totalMealCount }).map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full ${
                  i < recipeDetails.size ? 'bg-toast-gold' : 'bg-gray-300'
                } ${i >= recipeDetails.size && i === recipeDetails.size ? 'animate-pulse' : ''}`}
              />
            ))}
          </div>
        </div>

        {/* Skeleton Loading Cards */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="max-w-4xl mx-auto">
            {/* Status message */}
            <div className="text-center mb-6">
              <ToastyLoading
                alt={isResuming ? 'Toasty is loading your meal plan' : 'Toasty is creating your meal plan'}
                className="mx-auto mb-3 w-16 h-16"
              />
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {isResuming ? 'Loading Your Meal Plan' : 'Creating Your Meal Plan'}
              </h3>
              <p className="text-sm text-gray-600">
                {recipeDetails.size > 0
                  ? `Loaded ${recipeDetails.size} of ${totalMealCount} recipes...`
                  : isResuming
                    ? 'Fetching your saved recipes...'
                    : 'Toasty is crafting personalized recipes...'
                }
              </p>
            </div>

            {/* Skeleton recipe cards - Exact count (Issue #33) */}
            {Array.from({ length: totalMealCount }).map((_, index) => (
              <RecipeCardSkeleton key={index} badgeCount={3} />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-screen md:min-h-[600px] w-full max-w-none bg-warm-cream">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 flex items-center justify-between px-4 py-3 border-b bg-white shadow-sm">
        {/* Left: Back button */}
        <Button variant="ghost" size="default" onClick={onBack} className="flex items-center gap-1">
          <ICONS.navigation.back className={getIconSize('sm')} />
          <span className="hidden sm:inline">Back</span>
        </Button>

        {/* Center: People + Meal summary */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="flex items-center gap-1">
            <ICONS.user.users className={getIconSize('sm')} />
            <span>{sessionPreferences?.numberOfPeople || 2}</span>
          </span>
          <span className="text-gray-400">•</span>
          <span>
            {totalMealCount} meal{totalMealCount !== 1 ? 's' : ''}
          </span>
          {error && <ICONS.feedback.warning className={`${getIconSize('sm')} ${getIconColor('error')} ml-2`} />}
        </div>

        {/* Right: Progress dots + Actions menu */}
        <div className="flex items-center gap-3">
          {/* Progress dots - Always visible (Issue #33) */}
          <div className="flex items-center gap-1">
            {Array.from({ length: totalMealCount }).map((_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${
                  i < recipeDetails.size ? 'bg-toast-gold' : 'bg-gray-300'
                }`}
                aria-label={`Recipe ${i + 1} ${i < recipeDetails.size ? 'complete' : 'pending'}`}
                title={`${i < recipeDetails.size ? 'Complete' : 'Pending'}`}
              />
            ))}
          </div>

          {/* Actions dropdown menu */}
          <div className="relative">
            <button
              onClick={() => setShowDebug(!showDebug)}
              className="p-2.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              title="Debug & Settings"
            >
              <ICONS.actions.more className={getIconSize('sm')} />
            </button>
          </div>
        </div>
      </div>

      {/* Meal Plan Display - Tabbed interface - Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        <MealPlanDisplay
          recipeDetails={recipeDetails}
          loadingRecipeDetails={loadingRecipeDetails}
          onRerollRecipe={handleRerollRecipe}
          onRemoveRecipe={handleRemoveRecipe}
          onAddSlot={handleAddSlot}
          addingSlotForMealType={addingSlotForMealType}
          mealTypes={sessionPreferences?.mealTypes.filter(mt => mt.enabled).map(mt => mt.type)}
          onNavigateToSubscription={onNavigateToSubscription}
          isPolling={isPolling}
        />
      </div>

      {/* Fixed Footer - Clean ~64px bar with Continue button */}
      <div className="sticky bottom-0 z-10 border-t bg-white shadow-lg">
        <div className="px-4 py-3">
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleContinueToShopping}
            disabled={!onContinue || loadingRecipeDetails.size > 0}
          >
            {loadingRecipeDetails.size > 0
              ? `Loading recipes... (${loadingRecipeDetails.size} remaining)`
              : 'Continue to Shopping List →'
            }
          </Button>
          {error && (
            <p className="text-xs text-red-500 mt-1">Something went wrong. Please try again.</p>
          )}
        </div>
      </div>

      {/* Debug Console */}
      {showDebug && (
        <div className="border-t bg-gray-900 text-green-400 p-4 font-mono text-xs max-h-40 overflow-y-auto">
          <div className="flex justify-between items-center mb-2">
            <span className="text-green-300 font-semibold">Debug Console</span>
            <button
              onClick={() => setDebugLogs([])}
              className="px-2 py-1 bg-gray-700 text-green-300 rounded text-xs hover:bg-gray-600"
            >
              Clear
            </button>
          </div>
          {debugLogs.length === 0 ? (
            <div className="text-gray-500">No debug logs yet...</div>
          ) : (
            debugLogs.map((log, index) => (
              <div key={index} className="mb-1">
                {log}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}